import * as React from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { Button } from '../Button';
import { Input } from '../Input';
import { Tooltip } from '../Tooltip';
import { cn } from '../../lib/utils';
import useI18n from '../../hooks/useI18n';

interface EditableChipProps {
  value: string;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  onSave: (value: string) => string | null | undefined;
  onRemove: () => void;
}

function useChipEditor(value: string, onSave: EditableChipProps['onSave']) {
  const [draft, setDraft] = React.useState<string | null>(null);
  const [error, setError] = React.useState('');
  const restoreFocus = React.useRef(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const finish = () => {
    restoreFocus.current = true;
    setDraft(null);
    setError('');
  };

  const save = () => {
    const message = onSave((draft ?? value).trim());
    if (message) {
      setError(message);
      inputRef.current?.focus();
      return;
    }
    finish();
  };

  const focusEditButton = React.useCallback(
    (button: HTMLButtonElement | null) => {
      if (button && restoreFocus.current) {
        button.focus();
        restoreFocus.current = false;
      }
    },
    [],
  );

  const focusInput = React.useCallback((input: HTMLInputElement | null) => {
    inputRef.current = input;
    input?.focus();
  }, []);

  return { draft, setDraft, error, finish, save, focusEditButton, focusInput };
}

export function EditableChip({
  value,
  disabled,
  className,
  labelClassName,
  onSave,
  onRemove,
}: EditableChipProps) {
  const { t } = useI18n();
  const { draft, setDraft, error, finish, save, focusEditButton, focusInput } =
    useChipEditor(value, onSave);
  const errorId = React.useId();
  const editLabel = `${t('edit')} ${value}`;

  return (
    <div
      data-slot='editable-chip'
      onKeyDown={(event) => {
        if (
          draft !== null &&
          event.key === 'Escape' &&
          !event.nativeEvent.isComposing
        ) {
          event.preventDefault();
          event.stopPropagation();
          finish();
        }
      }}
      className={cn(
        'eye:flex eye:max-w-full eye:min-w-0 eye:flex-wrap eye:items-center eye:gap-4 eye:rounded-md eye:border eye:border-grey-40 eye:bg-grey-20 eye:px-8 eye:py-4',
        draft !== null && 'eye:w-full',
        className,
      )}
    >
      {draft === null ? (
        <>
          <span
            className={cn(
              'eye:text-grey-140 eye:min-w-0 eye:text-14 eye:wrap-anywhere',
              labelClassName,
            )}
          >
            {value}
          </span>
          <Tooltip content={t('edit')}>
            <Button
              ref={focusEditButton}
              type='button'
              variant='ghost'
              size='icon-xs'
              data-slot='chip-edit'
              disabled={disabled}
              aria-label={editLabel}
              onClick={() => setDraft(value)}
            >
              <Pencil aria-hidden />
            </Button>
          </Tooltip>
          <Tooltip content={t('delete')}>
            <Button
              type='button'
              variant='ghost'
              size='icon-xs'
              disabled={disabled}
              aria-label={`${t('delete')} ${value}`}
              onClick={onRemove}
            >
              <X aria-hidden />
            </Button>
          </Tooltip>
        </>
      ) : (
        <>
          <Input
            ref={focusInput}
            value={draft}
            disabled={disabled}
            aria-label={editLabel}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            className='eye:h-32 eye:flex-1 eye:bg-white eye:px-8 eye:text-14'
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing) return;
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                save();
              }
            }}
          />
          <div className='eye:ml-auto eye:flex eye:gap-4'>
            <Tooltip content={t('cancel')}>
              <Button
                type='button'
                variant='ghost'
                size='icon-xs'
                disabled={disabled}
                aria-label={`${t('cancel')} ${value}`}
                onClick={finish}
              >
                <X aria-hidden />
              </Button>
            </Tooltip>
            <Tooltip content={t('save')}>
              <Button
                type='button'
                variant='ghost'
                size='icon-xs'
                disabled={disabled}
                aria-label={`${t('save')} ${value}`}
                onClick={save}
              >
                <Check aria-hidden />
              </Button>
            </Tooltip>
          </div>
          {error && (
            <p
              id={errorId}
              role='alert'
              className='eye:w-full eye:text-14 eye:text-red-100'
            >
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
